import { useEffect, useState, useMemo } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { CreditCard, Eye, Loader2, Plus, Calendar, Users, List, Lock, Copy, Check, Key, Trash2, Gavel, X, AlertTriangle, RefreshCw, Receipt, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import BreederCalendar from "@/components/BreederCalendar";
import BreederWaitlist from "@/components/BreederWaitlist";
import BreederInsights from "@/components/BreederInsights";
import { logger } from "@/utils/logger";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BreederDashboard = ({ breederProfile, checkAuth }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("listings");
  const location = useLocation();
  
  // Auction modal state
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [auctionForm, setAuctionForm] = useState({
    starting_bid: "",
    reserve_price: "",
    buy_now_price: "",
    duration_days: 5,
    payment_window_hours: 48,
    sale_method: "invoice",
    lot_listing_ids: [],
    lot_title: "",
    video_url: "",
  });
  const [creatingAuction, setCreatingAuction] = useState(false);

  // Subscription/billing state
  const [subStatus, setSubStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    fetchMyListings();
    fetchConnectStatus();

    const params = new URLSearchParams(location.search);
    if (params.get('connect') === 'return') {
      fetchConnectStatus();
      checkAuth();
    }
  }, [location]);

  const fetchMyListings = async () => {
    try {
      const response = await axios.get(`${API}/listings/my`, { withCredentials: true });
      setListings(response.data.listings);
    } catch (error) {
      logger.error("Failed to fetch listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConnectStatus = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/status`, { withCredentials: true });
      setConnectStatus(response.data);
    } catch (error) {
      logger.error("Failed to fetch connect status:", error);
    }
  };

  const handleSetupPayouts = async () => {
    setConnectLoading(true);
    try {
      const response = await axios.post(`${API}/stripe/connect/onboarding-link`, {
        origin_url: window.location.origin
      }, { withCredentials: true });

      window.location.href = response.data.url;
    } catch (error) {
      logger.error("Failed to create onboarding link:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to start payout setup";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to start payout setup");
      setConnectLoading(false);
    }
  };

  const handleViewDashboard = async () => {
    try {
      const response = await axios.get(`${API}/stripe/connect/dashboard-link`, { withCredentials: true });
      window.open(response.data.url, '_blank');
    } catch (error) {
      logger.error("Failed to open Stripe dashboard:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to open Stripe dashboard";
      toast.error(typeof errorMessage === 'string' ? errorMessage : "Failed to open Stripe dashboard");
    }
  };

  const openAuctionModal = (listing) => {
    setSelectedListing(listing);
    setAuctionForm({
      starting_bid: listing.price ? Math.floor(listing.price * 0.5).toString() : "",
      reserve_price: listing.price?.toString() || "",
      buy_now_price: listing.price ? Math.floor(listing.price * 1.2).toString() : "",
      duration_days: 5,
      payment_window_hours: 48,
      sale_method: "invoice",
      lot_listing_ids: [],
      lot_title: "",
      video_url: "",
    });
    setShowAuctionModal(true);
  };

  const handleCreateAuction = async () => {
    if (!auctionForm.starting_bid) {
      toast.error("Starting bid is required");
      return;
    }

    setCreatingAuction(true);
    try {
      const payload = {
        listing_id: selectedListing.listing_id,
        starting_bid: parseFloat(auctionForm.starting_bid),
        reserve_price: auctionForm.reserve_price ? parseFloat(auctionForm.reserve_price) : null,
        buy_now_price: auctionForm.buy_now_price ? parseFloat(auctionForm.buy_now_price) : null,
        duration_days: auctionForm.duration_days,
        payment_window_hours: auctionForm.payment_window_hours,
        sale_method: auctionForm.sale_method,
        lot_listing_ids: auctionForm.lot_listing_ids,
        lot_title: auctionForm.lot_title || null,
        video_url: auctionForm.video_url || null,
      };

      const response = await axios.post(`${API}/auctions`, payload, { withCredentials: true });
      toast.success("Auction created! Bidding is now live.");
      setShowAuctionModal(false);
      fetchMyListings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create auction");
    } finally {
      setCreatingAuction(false);
    }
  };

  if (!breederProfile) {
    return <Navigate to="/onboarding/breeder" replace />;
  }

  const fetchSubscriptionStatus = async () => {
    setSubLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/status`, { withCredentials: true });
      setSubStatus(res.data);
    } catch (err) {
      logger.error("Failed to fetch subscription status:", err);
    } finally {
      setSubLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(res.data.message);
      setShowCancelModal(false);
      fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivateLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/reactivate`, {}, { withCredentials: true });
      toast.success(res.data.message);
      fetchSubscriptionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setReactivateLoading(false);
    }
  };

  const getConnectStatusBadge = () => {
    const status = connectStatus?.status || breederProfile?.connect_status || 'not_started';
    switch (status) {
      case 'complete':
        return <span className="px-3 py-1 text-xs rounded-full bg-green-500/20 text-green-400">Complete</span>;
      case 'pending':
        return <span className="px-3 py-1 text-xs rounded-full bg-yellow-500/20 text-yellow-400">In Progress</span>;
      default:
        return <span className="px-3 py-1 text-xs rounded-full bg-red-500/20 text-red-400">Not Started</span>;
    }
  };

  return (
    <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-2">Breeder Dashboard</p>
            <h1 className="text-4xl text-[#EDEDED]">{breederProfile.business_name}</h1>
          </div>
          <Link to="/dashboard/breeder/new-listing" className="btn-gold flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Listing
          </Link>
        </div>

        <div className="glass-card p-6 mb-8" data-testid="payout-setup-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CreditCard className="w-8 h-8 text-[#D4AF37]" />
              <div>
                <h3 className="text-xl text-[#EDEDED]">Payout Setup</h3>
                <p className="text-sm text-[#A1A1AA]">
                  {connectStatus?.status === 'complete'
                    ? 'Your payouts are configured and ready to receive funds.'
                    : 'Complete setup to receive payments from buyers.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {getConnectStatusBadge()}
              {connectStatus?.status === 'complete' ? (
                <button onClick={handleViewDashboard} className="btn-secondary text-sm" data-testid="view-stripe-dashboard">
                  View Stripe Dashboard
                </button>
              ) : (
                <button
                  onClick={handleSetupPayouts}
                  disabled={connectLoading}
                  className="btn-gold text-sm flex items-center gap-2"
                  data-testid="setup-payouts-btn"
                >
                  {connectLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                  Set Up Payouts
                </button>
              )}
            </div>
          </div>

          {connectStatus?.status !== 'complete' && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-400">⚠️ You must complete payout setup before buyers can purchase your listings.</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-4 text-center">
            <p className="text-3xl text-[#D4AF37]">{breederProfile.total_listings || 0}</p>
            <p className="text-sm text-[#A1A1AA]">Total Listings</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl text-[#D4AF37]">{breederProfile.total_sales || 0}</p>
            <p className="text-sm text-[#A1A1AA]">Sales</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl text-[#D4AF37] capitalize">{breederProfile.subscription_tier}</p>
            <p className="text-sm text-[#A1A1AA]">Tier</p>
          </div>
          <div className="glass-card p-4 text-center">
            <p className="text-3xl text-[#D4AF37]">{breederProfile.is_verified ? '✓' : '○'}</p>
            <p className="text-sm text-[#A1A1AA]">Verified</p>
          </div>
        </div>

        {/* Dashboard Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[#27272A] pb-4 overflow-x-auto">
          <button
            onClick={() => setActiveTab("listings")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === "listings" 
                ? "bg-[#D4AF37] text-black" 
                : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
            }`}
            data-testid="tab-listings"
          >
            <List className="w-4 h-4" />
            Listings
          </button>
          {(breederProfile.subscription_tier === "royalty" || breederProfile.subscription_tier === "elite") && (
            <button
              onClick={() => setActiveTab("private")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeTab === "private" 
                  ? "bg-[#D4AF37] text-black" 
                  : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
              }`}
              data-testid="tab-private"
            >
              <Lock className="w-4 h-4" />
              Private Listings
            </button>
          )}
          <button
            onClick={() => setActiveTab("calendar")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === "calendar" 
                ? "bg-[#D4AF37] text-black" 
                : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
            }`}
            data-testid="tab-calendar"
          >
            <Calendar className="w-4 h-4" />
            Calendar
          </button>
          <button
            onClick={() => setActiveTab("waitlist")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === "waitlist" 
                ? "bg-[#D4AF37] text-black" 
                : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
            }`}
            data-testid="tab-waitlist"
          >
            <Users className="w-4 h-4" />
            Waitlist
          </button>
          <button
            onClick={() => { setActiveTab("billing"); fetchSubscriptionStatus(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === "billing" 
                ? "bg-[#D4AF37] text-black" 
                : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
            }`}
            data-testid="tab-billing"
          >
            <Receipt className="w-4 h-4" />
            Billing
          </button>
          <button
            onClick={() => setActiveTab("insights")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
              activeTab === "insights" 
                ? "bg-[#D4AF37] text-black" 
                : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#18181B]"
            }`}
            data-testid="tab-insights"
          >
            <BarChart3 className="w-4 h-4" />
            Insights
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "listings" && (
          <div className="glass-card p-6">
            <h3 className="text-xl text-[#EDEDED] mb-4">My Listings</h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
            </div>
          ) : listings.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#A1A1AA] mb-4">No listings yet</p>
              <Link to="/dashboard/breeder/new-listing" className="btn-secondary text-sm">Create Your First Listing</Link>
            </div>
          ) : (
            <div className="space-y-4">
              {listings.map((listing) => (
                <div key={listing.listing_id} className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg">
                  <img src={listing.photos?.[0] || "https://via.placeholder.com/100"} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-[#EDEDED]">{listing.title}</p>
                      {listing.is_auction && (
                        <span className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded-full flex items-center gap-1">
                          <Gavel className="w-3 h-3" /> Auction
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#A1A1AA]">${listing.price?.toLocaleString()} • {listing.breed}</p>
                    {listing.is_private_listing && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#D4AF37]">
                        <Lock className="w-3 h-3" /> Private
                      </span>
                    )}
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      listing.status === 'active' ? 'bg-green-500/20 text-green-400' :
                      listing.status === 'draft' ? 'bg-yellow-500/20 text-yellow-400' :
                      listing.status === 'sold' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    }`}
                  >
                    {listing.status}
                  </span>
                  {listing.status === 'active' && !listing.is_auction && (
                    <button
                      onClick={() => openAuctionModal(listing)}
                      className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                      title="Start Auction"
                      data-testid={`start-auction-${listing.listing_id}`}
                    >
                      <Gavel className="w-5 h-5" />
                    </button>
                  )}
                  {listing.is_auction && (
                    <Link
                      to={`/auctions/${listing.auction_id}`}
                      className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-lg transition-colors"
                      title="View Auction"
                    >
                      <Gavel className="w-5 h-5" />
                    </Link>
                  )}
                  <Link to={`/listing/${listing.listing_id}`} className="text-[#71717A] hover:text-[#D4AF37]">
                    <Eye className="w-5 h-5" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {activeTab === "private" && (
          <PrivateListingsTab listings={listings.filter(l => l.is_private_listing)} onRefresh={fetchMyListings} />
        )}

        {activeTab === "calendar" && (
          <BreederCalendar />
        )}

        {activeTab === "waitlist" && (
          <BreederWaitlist />
        )}

        {activeTab === "billing" && (
          <BillingTab
            subStatus={subStatus}
            subLoading={subLoading}
            breederProfile={breederProfile}
            showCancelModal={showCancelModal}
            setShowCancelModal={setShowCancelModal}
            cancelLoading={cancelLoading}
            handleCancelSubscription={handleCancelSubscription}
            reactivateLoading={reactivateLoading}
            handleReactivateSubscription={handleReactivateSubscription}
          />
        )}

        {activeTab === "insights" && (
          <BreederInsights />
        )}
      </div>
    </div>
  );
};


// Billing Tab Component
const TIER_PRICES = {
  free: "$0/mo",
  basic: "$10.99/mo",
  starter: "$19.99/mo",
  professional: "$29.99/mo",
  elite: "$29.99/mo",
  royalty: "$99.99/mo",
};

const BillingTab = ({
  subStatus,
  subLoading,
  breederProfile,
  showCancelModal,
  setShowCancelModal,
  cancelLoading,
  handleCancelSubscription,
  reactivateLoading,
  handleReactivateSubscription,
}) => {
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [digestLoading, setDigestLoading] = useState(false);

  const tier = subStatus?.tier || breederProfile?.subscription_tier || "free";
  const isFree = tier === "free";
  const isPendingCancel = subStatus?.cancel_at_period_end;
  const periodEnd = subStatus?.current_period_end;

  const formattedEndDate = periodEnd
    ? new Date(periodEnd).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  useEffect(() => {
    const fetchPrefs = async () => {
      try {
        const res = await axios.get(`${API}/breeder/preferences`, { withCredentials: true });
        setDigestEnabled(res.data.weekly_digest_enabled);
      } catch (err) { logger.error(err); }
    };
    fetchPrefs();
  }, []);

  const toggleDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await axios.patch(`${API}/breeder/preferences/digest`, { weekly_digest_enabled: !digestEnabled }, { withCredentials: true });
      setDigestEnabled(res.data.weekly_digest_enabled);
      toast.success(res.data.message);
    } catch (err) {
      logger.error(err);
      toast.error("Failed to update preference");
    } finally {
      setDigestLoading(false);
    }
  };

  if (subLoading) {
    return (
      <div className="glass-card p-8 flex justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="glass-card p-6" data-testid="billing-current-plan">
        <h3 className="text-xl text-[#EDEDED] mb-5 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-[#D4AF37]" />
          Subscription & Billing
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg bg-[#18181B]">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-1">Current Plan</p>
            <p className="text-2xl text-[#EDEDED] capitalize font-semibold">{tier}</p>
            <p className="text-sm text-[#A1A1AA] mt-1">{TIER_PRICES[tier] || "$0/mo"}</p>
          </div>
          <div className="p-4 rounded-lg bg-[#18181B]">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-1">Status</p>
            {isPendingCancel ? (
              <>
                <p className="text-lg text-amber-400 font-semibold">Cancelling</p>
                <p className="text-sm text-[#A1A1AA] mt-1">Active until {formattedEndDate || "period end"}</p>
              </>
            ) : (
              <>
                <p className="text-lg text-green-400 font-semibold">Active</p>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  {isFree ? "No billing cycle" : formattedEndDate ? `Renews ${formattedEndDate}` : "Recurring"}
                </p>
              </>
            )}
          </div>
          <div className="p-4 rounded-lg bg-[#18181B]">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-1">Platform Fee</p>
            <p className="text-2xl text-[#EDEDED] font-semibold">
              {tier === "royalty" ? "1%" : tier === "free" ? "10%" : "3%"}
            </p>
            <p className="text-sm text-[#A1A1AA] mt-1">Per transaction</p>
          </div>
          <div className="p-4 rounded-lg bg-[#18181B]" data-testid="annual-fee-card">
            <p className="text-xs text-[#71717A] uppercase tracking-wider mb-1">Annual Registration</p>
            {isFree ? (
              <>
                <p className="text-lg text-[#71717A] font-semibold">N/A</p>
                <p className="text-sm text-[#A1A1AA] mt-1">Paid plans only</p>
              </>
            ) : subStatus?.annual_fee?.status === "waived" ? (
              <>
                <p className="text-lg text-[#22C55E] font-semibold">Waived</p>
                <p className="text-sm text-[#A1A1AA] mt-1">
                  Founding Member ({subStatus.annual_fee.waived_years}yr)
                </p>
              </>
            ) : (
              <>
                <p className="text-2xl text-[#EDEDED] font-semibold">$59.99</p>
                <p className="text-sm text-[#A1A1AA] mt-1">/year</p>
              </>
            )}
          </div>
        </div>

        {/* Pending Cancellation Banner */}
        {isPendingCancel && (
          <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-6" data-testid="billing-cancel-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-amber-200 font-medium">Your subscription is set to cancel</p>
                <p className="text-sm text-amber-300/70 mt-1">
                  You'll retain full access to your <span className="capitalize">{tier}</span> plan features until{" "}
                  <strong>{formattedEndDate}</strong>. After that, your account will revert to the Free tier.
                </p>
                <button
                  onClick={handleReactivateSubscription}
                  disabled={reactivateLoading}
                  className="mt-3 px-4 py-2 bg-[#D4AF37] text-black text-sm rounded-lg hover:bg-[#B8962E] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="billing-reactivate-btn"
                >
                  {reactivateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Reactivate Subscription
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade / Cancel Actions */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-[#27272A]">
          <Link
            to="/subscription-plans"
            className="px-4 py-2 bg-[#D4AF37] text-black text-sm rounded-lg hover:bg-[#B8962E] transition-colors"
            data-testid="billing-change-plan-btn"
          >
            {isFree ? "Upgrade Plan" : "Change Plan"}
          </Link>

          {!isFree && !isPendingCancel && subStatus?.stripe_subscription_id && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-4 py-2 text-sm rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="billing-cancel-btn"
            >
              Cancel Subscription
            </button>
          )}
        </div>
      </div>

      {/* Annual Registration Fee Detail */}
      {!isFree && subStatus?.annual_fee && (
        <div className="glass-card p-6" data-testid="annual-fee-detail">
          <h3 className="text-lg text-[#EDEDED] mb-4">Annual Registration Fee</h3>
          <div className="p-4 bg-[#18181B] rounded-lg">
            {subStatus.annual_fee.status === "waived" ? (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
                  <Check className="w-4 h-4 text-[#22C55E]" />
                </div>
                <div>
                  <p className="text-[#EDEDED] font-medium">Founding Member — Annual Fee Waived</p>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    Your $59.99/year annual registration fee is waived for{" "}
                    <strong className="text-[#D4AF37]">{subStatus.annual_fee.waived_years} year{subStatus.annual_fee.waived_years > 1 ? "s" : ""}</strong> as 
                    a Tier {subStatus.annual_fee.founding_tier} Founding Member.
                  </p>
                  {subStatus.annual_fee.next_due_date && (
                    <p className="text-xs text-[#71717A] mt-2">
                      Waiver expires: {new Date(subStatus.annual_fee.next_due_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#EDEDED] font-medium">$59.99/year</p>
                  <p className="text-sm text-[#A1A1AA] mt-1">
                    Maintains your verified breeder status, verified badge, and platform access.
                  </p>
                  {subStatus.annual_fee.next_due_date && (
                    <p className="text-xs text-[#71717A] mt-1">
                      Next due: {new Date(subStatus.annual_fee.next_due_date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                  )}
                </div>
                <p className="text-2xl text-[#D4AF37] font-bold">$59.99</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Preferences */}
      <div className="glass-card p-6" data-testid="email-preferences">
        <h3 className="text-lg text-[#EDEDED] mb-4">Email Preferences</h3>
        <div className="flex items-center justify-between p-4 bg-[#18181B] rounded-lg">
          <div>
            <p className="text-sm text-[#EDEDED]">Weekly Insights Digest</p>
            <p className="text-xs text-[#71717A] mt-0.5">Receive a weekly summary of your listing views, bids, and inquiries every Monday.</p>
          </div>
          <button
            onClick={toggleDigest}
            disabled={digestLoading}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              digestEnabled ? "bg-[#D4AF37]" : "bg-[#3F3F46]"
            }`}
            data-testid="digest-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              digestEnabled ? "translate-x-6" : "translate-x-1"
            }`} />
          </button>
        </div>
      </div>

      {/* Cancellation Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowCancelModal(false)}>
          <div className="glass-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="cancel-subscription-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-xl text-[#EDEDED]">Cancel Subscription?</h3>
                <p className="text-sm text-[#71717A]">This action can be undone before the period ends.</p>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-[#18181B] mb-6 text-sm text-[#A1A1AA] space-y-2">
              <p>Your <span className="capitalize text-[#EDEDED]">{tier}</span> subscription will remain active until the end of your current billing period.</p>
              <p>After that, your account will revert to the <strong className="text-[#EDEDED]">Free</strong> tier with:</p>
              <ul className="list-disc list-inside text-[#71717A] space-y-1 ml-2">
                <li>2 listing limit</li>
                <li>10% platform fee</li>
                <li>No verified badge</li>
              </ul>
              <p className="text-xs text-[#52525B] mt-3">You can reactivate anytime before the period ends to keep your current plan.</p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2.5 text-sm rounded-lg border border-[#27272A] text-[#A1A1AA] hover:bg-[#18181B] transition-colors"
                data-testid="cancel-modal-keep-btn"
              >
                Keep My Plan
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="flex-1 py-2.5 text-sm rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="cancel-modal-confirm-btn"
              >
                {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// Private Listings Tab Component
const PrivateListingsTab = ({ listings, onRefresh }) => {
  const [selectedListing, setSelectedListing] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [newInviteConfig, setNewInviteConfig] = useState({ max_uses: 1, expires_days: 7 });

  const fetchInvites = async (listingId) => {
    setLoadingInvites(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      const res = await axios.get(`${API}/listings/${listingId}/invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(res.data.invites || []);
    } catch (err) {
      logger.error("Failed to fetch invites:", err);
      toast.error("Failed to load invite codes");
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleSelectListing = (listing) => {
    setSelectedListing(listing);
    fetchInvites(listing.listing_id);
  };

  const handleGenerateCode = async () => {
    if (!selectedListing) return;
    
    setGeneratingCode(true);
    try {
      const token = sessionStorage.getItem("auth_token");
      const res = await axios.post(
        `${API}/listings/${selectedListing.listing_id}/generate-invite`,
        {
          invite_type: "code",
          max_uses: newInviteConfig.max_uses,
          expires_days: newInviteConfig.expires_days
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Invite code generated: ${res.data.invite_code}`);
      fetchInvites(selectedListing.listing_id);
    } catch (err) {
      logger.error("Failed to generate code:", err);
      toast.error(err.response?.data?.detail || "Failed to generate invite code");
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyToClipboard = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success("Code copied to clipboard!");
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      toast.error("Failed to copy code");
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!confirm("Are you sure you want to revoke this invite code?")) return;
    
    try {
      const token = sessionStorage.getItem("auth_token");
      await axios.delete(`${API}/invites/${inviteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Invite code revoked");
      fetchInvites(selectedListing.listing_id);
    } catch (err) {
      logger.error("Failed to revoke invite:", err);
      toast.error("Failed to revoke invite");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Private Listings List */}
      <div className="glass-card p-6">
        <h3 className="text-xl text-[#EDEDED] mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-[#D4AF37]" />
          Private Listings
        </h3>
        
        {listings.length === 0 ? (
          <div className="text-center py-8">
            <Lock className="w-12 h-12 text-[#3F3F46] mx-auto mb-3" />
            <p className="text-[#A1A1AA] mb-2">No private listings yet</p>
            <p className="text-[#71717A] text-sm mb-4">Create a listing and set visibility to "Private"</p>
            <Link to="/dashboard/breeder/new-listing" className="btn-gold text-sm">
              <Plus className="w-4 h-4 inline mr-1" /> Create Private Listing
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            {listings.map((listing) => (
              <button
                key={listing.listing_id}
                onClick={() => handleSelectListing(listing)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedListing?.listing_id === listing.listing_id
                    ? "bg-[#D4AF37]/10 border border-[#D4AF37]/30"
                    : "bg-[#18181B] hover:bg-[#27272A]"
                }`}
              >
                <p className="text-[#EDEDED] truncate">{listing.title}</p>
                <p className="text-xs text-[#71717A]">{listing.breed} • ${listing.price?.toLocaleString()}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Invite Codes Management */}
      <div className="lg:col-span-2 glass-card p-6">
        {!selectedListing ? (
          <div className="text-center py-12">
            <Key className="w-12 h-12 text-[#3F3F46] mx-auto mb-3" />
            <p className="text-[#A1A1AA]">Select a private listing to manage invite codes</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl text-[#EDEDED]">{selectedListing.title}</h3>
                <p className="text-sm text-[#71717A]">Manage access codes for this listing</p>
              </div>
              <Link to={`/listing/${selectedListing.listing_id}`} className="text-[#D4AF37] text-sm hover:underline">
                View Listing →
              </Link>
            </div>

            {/* Generate New Code */}
            <div className="p-4 rounded-lg bg-[#18181B] mb-6">
              <h4 className="text-[#EDEDED] font-medium mb-3">Generate New Invite Code</h4>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">Max Uses</label>
                  <select
                    value={newInviteConfig.max_uses}
                    onChange={(e) => setNewInviteConfig(prev => ({ ...prev, max_uses: parseInt(e.target.value) }))}
                    className="input-minimal text-sm"
                  >
                    <option value={1}>1 use</option>
                    <option value={5}>5 uses</option>
                    <option value={10}>10 uses</option>
                    <option value={25}>25 uses</option>
                    <option value={100}>100 uses</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#71717A] mb-1">Expires In</label>
                  <select
                    value={newInviteConfig.expires_days}
                    onChange={(e) => setNewInviteConfig(prev => ({ ...prev, expires_days: parseInt(e.target.value) }))}
                    className="input-minimal text-sm"
                  >
                    <option value={1}>1 day</option>
                    <option value={7}>7 days</option>
                    <option value={30}>30 days</option>
                    <option value={90}>90 days</option>
                    <option value={365}>1 year</option>
                  </select>
                </div>
              </div>
              <button
                onClick={handleGenerateCode}
                disabled={generatingCode}
                className="w-full py-2 bg-[#D4AF37] text-black rounded-lg hover:bg-[#B8962E] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="generate-code-btn"
              >
                {generatingCode ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Key className="w-4 h-4" />
                )}
                Generate Code
              </button>
            </div>

            {/* Existing Codes */}
            <h4 className="text-[#EDEDED] font-medium mb-3">Active Invite Codes</h4>
            {loadingInvites ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
              </div>
            ) : invites.length === 0 ? (
              <p className="text-[#71717A] text-center py-8">No invite codes generated yet</p>
            ) : (
              <div className="space-y-2">
                {invites.map((invite) => (
                  <div key={invite.invite_id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A]">
                    <div className="flex items-center gap-3">
                      <div className="font-mono text-lg text-[#D4AF37] tracking-wider">
                        {invite.invite_code}
                      </div>
                      <button
                        onClick={() => copyToClipboard(invite.invite_code)}
                        className="p-1.5 rounded hover:bg-[#27272A] transition-colors"
                      >
                        {copiedCode === invite.invite_code ? (
                          <Check className="w-4 h-4 text-green-400" />
                        ) : (
                          <Copy className="w-4 h-4 text-[#71717A]" />
                        )}
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right text-xs">
                        <p className="text-[#A1A1AA]">{invite.uses}/{invite.max_uses} uses</p>
                        <p className="text-[#52525B]">
                          {invite.expires_at 
                            ? `Expires ${new Date(invite.expires_at).toLocaleDateString()}`
                            : "No expiration"}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invite.status === "pending" ? "bg-green-500/20 text-green-400" :
                        invite.status === "accepted" ? "bg-blue-500/20 text-blue-400" :
                        "bg-gray-500/20 text-gray-400"
                      }`}>
                        {invite.status}
                      </span>
                      <button
                        onClick={() => handleRevokeInvite(invite.invite_id)}
                        className="p-1.5 rounded text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors"
                        title="Revoke"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Auction Creation Modal */}
      {showAuctionModal && selectedListing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowAuctionModal(false)}>
          <div className="glass-card p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                  <Gavel className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="text-xl text-[#EDEDED]">Start Auction</h3>
                  <p className="text-sm text-[#71717A]">{selectedListing.title}</p>
                </div>
              </div>
              <button onClick={() => setShowAuctionModal(false)} className="text-[#A1A1AA] hover:text-[#EDEDED]">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Starting Bid *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
                  <input
                    type="number"
                    value={auctionForm.starting_bid}
                    onChange={(e) => setAuctionForm({...auctionForm, starting_bid: e.target.value})}
                    className="input-minimal w-full pl-8"
                    placeholder="500"
                    data-testid="auction-starting-bid"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Reserve Price (optional, hidden)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
                  <input
                    type="number"
                    value={auctionForm.reserve_price}
                    onChange={(e) => setAuctionForm({...auctionForm, reserve_price: e.target.value})}
                    className="input-minimal w-full pl-8"
                    placeholder="Minimum to sell"
                  />
                </div>
                <p className="text-xs text-[#52525B] mt-1">Auction won't sell below this price</p>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Buy Now Price (optional)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]">$</span>
                  <input
                    type="number"
                    value={auctionForm.buy_now_price}
                    onChange={(e) => setAuctionForm({...auctionForm, buy_now_price: e.target.value})}
                    className="input-minimal w-full pl-8"
                    placeholder="Instant purchase price"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Duration</label>
                  <select
                    value={auctionForm.duration_days}
                    onChange={(e) => setAuctionForm({...auctionForm, duration_days: parseInt(e.target.value)})}
                    className="input-minimal w-full"
                  >
                    <option value={3}>3 Days</option>
                    <option value={5}>5 Days</option>
                    <option value={7}>7 Days</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Payment Window</label>
                  <select
                    value={auctionForm.payment_window_hours}
                    onChange={(e) => setAuctionForm({...auctionForm, payment_window_hours: parseInt(e.target.value)})}
                    className="input-minimal w-full"
                  >
                    <option value={24}>24 Hours</option>
                    <option value={48}>48 Hours</option>
                    <option value={72}>72 Hours</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Sale Method</label>
                <select
                  value={auctionForm.sale_method}
                  onChange={(e) => setAuctionForm({...auctionForm, sale_method: e.target.value})}
                  className="input-minimal w-full"
                  data-testid="sale-method-select"
                >
                  <option value="invoice">Invoice (Checkout Link)</option>
                  <option value="auto_charge">Auto-Charge Card</option>
                  <option value="manual">Manual (Messaging)</option>
                </select>
              </div>

              {/* Bundle / Lot Selection */}
              {(() => {
                const availableForLot = listings.filter(l => l.status === "active" && !l.is_auction && l.listing_id !== selectedListing?.listing_id);
                if (availableForLot.length === 0) return null;
                return (
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Bundle as Lot (optional)</label>
                  <p className="text-xs text-[#52525B] mb-2">Select additional listings to sell together as one lot</p>
                  <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-[#0A0A0A] rounded-lg">
                    {availableForLot.map((l) => (
                      <label key={l.listing_id} className="flex items-center gap-2 p-1.5 rounded hover:bg-[#18181B] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={auctionForm.lot_listing_ids.includes(l.listing_id)}
                          onChange={(e) => {
                            const ids = e.target.checked
                              ? [...auctionForm.lot_listing_ids, l.listing_id]
                              : auctionForm.lot_listing_ids.filter(id => id !== l.listing_id);
                            setAuctionForm({...auctionForm, lot_listing_ids: ids});
                          }}
                          className="accent-[#D4AF37]"
                          data-testid={`lot-checkbox-${l.listing_id}`}
                        />
                        <span className="text-sm text-[#EDEDED] truncate">{l.title}</span>
                        <span className="text-xs text-[#71717A] ml-auto">${l.price?.toLocaleString()}</span>
                      </label>
                    ))}
                  </div>
                  {auctionForm.lot_listing_ids.length > 0 && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={auctionForm.lot_title}
                        onChange={(e) => setAuctionForm({...auctionForm, lot_title: e.target.value})}
                        className="input-minimal w-full text-sm"
                        placeholder="Lot title (e.g. Spring Litter Bundle)"
                        data-testid="lot-title-input"
                      />
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Video URL */}
              <div>
                <label className="block text-sm text-[#A1A1AA] mb-2">Showcase Video (optional)</label>
                <input
                  type="url"
                  value={auctionForm.video_url}
                  onChange={(e) => setAuctionForm({...auctionForm, video_url: e.target.value})}
                  className="input-minimal w-full text-sm"
                  placeholder="YouTube or Vimeo URL"
                  data-testid="auction-video-url"
                />
                <p className="text-xs text-[#52525B] mt-1">Add a video to showcase the animal to bidders</p>
              </div>

              <div className="p-3 bg-[#18181B] rounded-lg text-sm text-[#A1A1AA]">
                <p className="mb-1"><strong className="text-[#EDEDED]">Auction ends in {auctionForm.duration_days} days</strong></p>
                {auctionForm.lot_listing_ids.length > 0 && (
                  <p className="mb-1 text-[#D4AF37]">Lot of {1 + auctionForm.lot_listing_ids.length} items</p>
                )}
                {auctionForm.video_url && (
                  <p className="mb-1 text-blue-400">Video showcase attached</p>
                )}
                <p>Winner has {auctionForm.payment_window_hours} hours to pay after auction ends.</p>
                <p className="text-xs mt-1 text-[#71717A]">
                  {auctionForm.sale_method === "auto_charge" && "Winner's card on file will be charged automatically."}
                  {auctionForm.sale_method === "invoice" && "Winner will receive a checkout link to complete payment."}
                  {auctionForm.sale_method === "manual" && "You and the winner will coordinate payment via Breedling messaging."}
                </p>
              </div>

              <div className="flex gap-4 pt-2">
                <button
                  onClick={() => setShowAuctionModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAuction}
                  disabled={creatingAuction || !auctionForm.starting_bid}
                  className="btn-gold flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="create-auction-btn"
                >
                  {creatingAuction ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" />}
                  Start Auction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreederDashboard;
